import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Image, Button } from 'tfc-components';

import feature from 'assets/images/home/features.png';
import whatwework from 'assets/images/home/whatwework.png';
import Icon from 'components/atoms/Icon';
import Section from 'components/organisms/Section';
import mapModifiers from 'utils/functions';

const Features: React.FC = () => {
  const [active, setActive] = useState(0);
  const navigation = useNavigate();
  return (
    <Section className="p-home_features">
      <Section.Title>Featured Product</Section.Title>
      <div className="p-home_whoWeAre p-home_whoWeAre-reverse">
        <div className="p-home_features_image">
          <Image imgSrc={feature} alt="Who we are" ratio={583 / 413} />
        </div>
        <div className="p-home_whoWeAre_content">
          <div className={mapModifiers('p-home_features_item', active === 0 && 'active')}>
            <div className="p-home_features_title" onClick={() => setActive(0)}>
              <Typography.Text
                fontweight="600"
                extendClasses="f-18x24"
              >
                EVOL AI Tutor
              </Typography.Text>
            </div>
            <Typography.Text fontweight="600" extendClasses="p-home_whoWeAre_text">
              Smarter studying, made personal.
            </Typography.Text>
            <Typography.Text extendClasses="p-home_whoWeAre_text">
              A personalized learning assistant that helps students study more effectively.
              With real-time adaptation to each learner’s strengths and weaknesses,
              EVOL AI Tutor empowers independent learning — and is already gaining
              traction in schools and tutoring platforms.
            </Typography.Text>
            <Typography.Text fontweight="600" extendClasses="p-home_whoWeAre_text">
              Key Features:
            </Typography.Text>
            <ul className="p-home_whoWeAre_list">
              <li>
                <Typography.Text>- AI-powered personalization engine</Typography.Text>
              </li>
              <li>
                <Typography.Text>- Smart quiz generation</Typography.Text>
              </li>
              <li>
                <Typography.Text>- Learning path tracking</Typography.Text>
              </li>
              <li>
                <Typography.Text>- Support for multiple languages</Typography.Text>
              </li>
            </ul>
            <Button onClick={() => navigation('/contact-us')} extendClasses="fButton fButton-sm p-home_features_button">
              <Typography.Text extendClasses="f-14x20" fontweight="600">Book a Demo</Typography.Text>
            </Button>
          </div>
          <div className={mapModifiers('p-home_features_item', active === 1 && 'active')}>
            <div className="p-home_features_title" onClick={() => setActive(1)}>
              <Typography.Text
                fontweight="600"
                extendClasses="f-18x24"
              >
                EVOL Field
              </Typography.Text>
            </div>
            <Typography.Text fontweight="600" extendClasses="p-home_whoWeAre_text">
              Real-time field operations, reimagined.
            </Typography.Text>
            <Typography.Text extendClasses="p-home_whoWeAre_text">
              A field force management system powered by AI + wearable tech.
              From attendance and route tracking to activity scoring using audio
              cues and geolocation — EVOL Field brings unprecedented visibility
              into frontline operations.
            </Typography.Text>
            <Typography.Text fontweight="600" extendClasses="p-home_whoWeAre_text">
              Use Cases:
            </Typography.Text>
            <ul className="p-home_whoWeAre_list">
              <li>
                <Typography.Text>- Retail sales team tracking</Typography.Text>
              </li>
              <li>
                <Typography.Text>- Merchandising check-ins</Typography.Text>
              </li>
              <li>
                <Typography.Text>- Tour guide and logistics monitoring</Typography.Text>
              </li>
              <li>
                <Typography.Text>
                  - Workforce anomaly detection
                  (GPS, time, behavior)
                </Typography.Text>
              </li>
            </ul>
            <Button extendClasses="fButton fButton-sm p-home_features_button">
              <Typography.Text extendClasses="f-14x20" fontweight="600">Explore EVOL Field</Typography.Text>
            </Button>
          </div>
          <div className={mapModifiers('p-home_features_item', active === 2 && 'active')}>
            <div className="p-home_features_title" onClick={() => setActive(2)}>
              <Typography.Text
                fontweight="600"
                extendClasses="f-18x24"
              >
                EVOL Askly
              </Typography.Text>
            </div>
            <Typography.Text fontweight="600" extendClasses="p-home_whoWeAre_text">
              Your internal AI copilot.
            </Typography.Text>
            <Typography.Text extendClasses="p-home_whoWeAre_text">
              Think ChatGPT — but trained specifically on your team’s
              knowledge base, documents, and workflows.
              EVOL Askly turns everyday questions into instant answers, helping teams work smarter.
            </Typography.Text>
            <Typography.Text fontweight="600" extendClasses="p-home_whoWeAre_text">
              Highlights:
            </Typography.Text>
            <ul className="p-home_whoWeAre_list">
              <li>
                <Typography.Text>- Private & secure enterprise-grade LLM</Typography.Text>
              </li>
              <li>
                <Typography.Text>
                  - Multi-source document ingestion
                  (Sharepoint, Drive, Uploaded…)
                </Typography.Text>
              </li>
              <li>
                <Typography.Text>- Context-aware memory for follow-up queries</Typography.Text>
              </li>
              <li>
                <Typography.Text>- Works in Zalo, MS Teams, or web interface...</Typography.Text>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="p-home_whoWeAre p-home_whoWeWork">
        <div className="p-home_whoWeWork_image">
          <Image imgSrc={whatwework} alt="Who we are" ratio={411 / 378} />
        </div>
        <div className="p-home_whoWeAre_content">
          <Section.Title left small>Who We Work With</Section.Title>
          <Typography.Text extendClasses="p-home_whoWeAre_text">
            We partner with:
          </Typography.Text>
          <ul className="p-home_whoWeAre_list">
            <li>
              <Icon iconName="checkOrange" size="24" />
              <Typography.Text>Forward-thinking startups</Typography.Text>
            </li>
            <li>
              <Icon iconName="checkOrange" size="24" />
              <Typography.Text>Internal innovation teams at large enterprises</Typography.Text>
            </li>
            <li>
              <Icon iconName="checkOrange" size="24" />
              <Typography.Text>Social impact organizations</Typography.Text>
            </li>
          </ul>
          <Typography.Text extendClasses="p-home_whoWeAre_text">
            We help them explore, validate, and launch
            {' '}
            <Typography.Text
              type="span"
              fontweight="600"
              extendClasses="p-home_whoWeAre_textBold"
            >
              AI-native solutions
            </Typography.Text>
            {' '}
            from 0 → 1.
            {' '}
          </Typography.Text>
        </div>
      </div>
    </Section>
  );
};

export default Features;
